import type { ChPostMetrics, MetricsItem } from './metrics';
import type { ChPost, ChPostContentType, ChPostPlatform, ChPostStatus, ChFollowers } from '../types';

/** A carousel can have up to 20 slides. */
export const MAX_MEDIA = 20;

/** Content that is a video and gets its own cover. */
export const VIDEO_CONTENT_TYPES: ChPostContentType[] = ['reels', 'video', 'short', 'episodio', 'stories'];

const PLATFORM_ORDER: ChPostPlatform[] = ['instagram', 'tiktok', 'youtube', 'podcast'];

// The list columns arrive with the v2 SQL; until then the single column is
// all there is

// Loose shapes: the client page gets plain strings from the share-link RPC

export function getPlatforms(post: { platform: string; platforms?: string[] | null }): ChPostPlatform[] {
  const list: string[] = post.platforms && post.platforms.length > 0 ? post.platforms : [post.platform];
  return PLATFORM_ORDER.filter(p => list.includes(p));
}

export function getAssigneeIds(post: Pick<ChPost, 'assignee_id' | 'assignee_ids'>): string[] {
  if (post.assignee_ids && post.assignee_ids.length > 0) return post.assignee_ids;
  return post.assignee_id ? [post.assignee_id] : [];
}

export function getMediaUrls(post: { media_url: string | null; media_urls?: string[] | null }): string[] {
  if (post.media_urls && post.media_urls.length > 0) return post.media_urls;
  return post.media_url ? [post.media_url] : [];
}

/** What a card shows: the designed cover, else the first file. */
export function getCoverUrl(post: { thumbnail_url: string | null; media_url: string | null; media_urls?: string[] | null }): string | null {
  return post.thumbnail_url || getMediaUrls(post)[0] || null;
}

/** Stats as summary input: one set per platform when there are several. */
export function getMetricsItem(post: { metrics?: ChPostMetrics | null; metrics_by_platform?: Partial<Record<string, ChPostMetrics>> | null }): MetricsItem {
  const byPlatform = Object.values(post.metrics_by_platform || {})
    .filter((m): m is ChPostMetrics => !!m && typeof m === 'object');
  return byPlatform.length > 1 ? byPlatform : post.metrics;
}

export function getTotalFollowers(followers: ChFollowers | null | undefined): number | null {
  if (!followers) return null;
  const values = Object.values(followers).filter((n): n is number => typeof n === 'number' && n >= 0);
  return values.length > 0 ? values.reduce((a, b) => a + b, 0) : null;
}

export type DeadlineKind = 'internal' | 'publication';

export interface Deadline {
  date: string;
  kind: DeadlineKind;
  /** Whole days from today: negative when late. */
  daysLeft: number;
  /** Already met: ready for review (internal) or published (publication). */
  met: boolean;
}

const READY_STATUSES: ChPostStatus[] = ['pronto', 'em_revisao', 'aprovado', 'publicado'];

function daysBetween(fromIso: string, toIso: string): number {
  const from = Date.UTC(+fromIso.slice(0, 4), +fromIso.slice(5, 7) - 1, +fromIso.slice(8, 10));
  const to = Date.UTC(+toIso.slice(0, 4), +toIso.slice(5, 7) - 1, +toIso.slice(8, 10));
  return Math.round((to - from) / 86_400_000);
}

export function todayIso(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The date the item is working towards. The "Prazo" is the internal one
 * (ready for review); without it, the publication date is the deadline.
 */
export function getDeadline(
  post: Pick<ChPost, 'due_date' | 'scheduled_date' | 'status'>,
  today = todayIso()
): Deadline | null {
  if (post.due_date) {
    const date = post.due_date.slice(0, 10);
    return { date, kind: 'internal', daysLeft: daysBetween(today, date), met: READY_STATUSES.includes(post.status) };
  }
  if (post.scheduled_date) {
    const date = post.scheduled_date.slice(0, 10);
    return { date, kind: 'publication', daysLeft: daysBetween(today, date), met: post.status === 'publicado' };
  }
  return null;
}

/** "Atrasado 2 dias" / "Hoje" / "Amanhã" while it matters; null otherwise. */
export function getDeadlineBadge(deadline: Deadline | null): { label: string; tone: 'late' | 'soon' } | null {
  if (!deadline || deadline.met) return null;
  const prefix = deadline.kind === 'internal' ? 'Prazo' : 'Publica';
  if (deadline.daysLeft < 0) {
    const days = -deadline.daysLeft;
    return { label: `Atrasado ${days} ${days === 1 ? 'dia' : 'dias'}`, tone: 'late' };
  }
  if (deadline.daysLeft === 0) return { label: `${prefix} hoje`, tone: 'soon' };
  if (deadline.daysLeft === 1) return { label: `${prefix} amanhã`, tone: 'soon' };
  return null;
}
